import { uniqueId } from 'lodash'
import { IconProps } from '@alto-avios/alto-ui/dist/components/Icon/Icon'
import { AncillariesLocation } from '@reward-platform/ancillaries-locations'
import { LocationOptionProps } from '@reward-platform/lift/components/AutoComplete/components/AutoComplete.types'

export type AutoCompleteSource = {
  key: string
  title: string
  subtitle?: string
  icon: IconProps
}

const getIcon = (fullName: string, type?: string): IconProps => {
  const icon: IconProps = {
    iconName: '',
    iconPrefix: 'far',
  }
  if (!type) {
    icon.iconName = 'location-dot'
  }

  const formattedType = type?.toLowerCase()

  if (fullName?.includes('Hotel')) {
    icon.iconName = 'bed'
  }
  switch (formattedType) {
    case 'point_of_interest':
      icon.iconName = 'camera'
      break
    case 'high_level_region':
      icon.iconName = 'crosshairs-simple'
      break
    case 'property':
      icon.iconName = 'house'
      break
    case 'airport':
      icon.iconName = 'plane'
      icon.iconPrefix = 'fas'
      break
    case 'neighborhood':
      icon.iconName = 'user-group'
      break
    case 'province_state':
      icon.iconName = 'flag'
      break
    case 'railway_bus_station':
      icon.iconName = 'train'
      icon.iconPrefix = 'fas'
      break
    case 'luxury_car_rental_station':
      icon.iconName = 'star'
      break
    case 'seaport_dock':
      icon.iconName = 'anchor'
      break
    case 'multi_city_vicinity':
    case 'city_downtown':
    case 'downtown':
    case 'downtown_off_airport':
    case 'delivery_collection_unit':
    case 'unmanned':
    case 'exhibition_conference_centre':
    case 'garage_hotel':
    case 'hq':
      icon.iconName = 'buildings'
      break
    default:
      icon.iconName = 'location-dot'
      break
  }

  return icon
}

export const createAutoCompleteSourceFromLocation = (
  locations: void | AncillariesLocation[] | undefined
): AutoCompleteSource[] =>
  locations?.map(({ fullName, id, type }) => {
    const splitIndex = fullName.indexOf(',')
    return {
      key: id,
      title: splitIndex > 0 ? fullName.substring(0, splitIndex) : fullName,
      subtitle: splitIndex > 0 ? fullName.substring(splitIndex + 1).trim() : '',
      icon: getIcon(fullName, type),
    }
  }) || []

const decodeHtmlEntities = (str: string) => {
  const parser = new DOMParser()
  const decodedString = parser.parseFromString(str, 'text/html').body.textContent

  return decodedString ?? str
}

export const createAutoCompleteLocationSource = (
  locations: void | (AncillariesLocation & { key?: string })[] | undefined,
  generateNewId?: boolean
): LocationOptionProps[] =>
  locations?.map((location) => {
    const { fullName, type, id } = location
    const newKey = uniqueId()
    const key = location.key ?? newKey
    location.key = key
    const splitIndex = fullName.indexOf(',')
    const decodedFullName = decodeHtmlEntities(fullName)

    return {
      key,
      value: generateNewId ? key : id,
      icon: getIcon(decodedFullName, type),
      label: decodedFullName,
      title: splitIndex > 0 ? decodedFullName.substring(0, splitIndex) : decodedFullName,
      subtitle: splitIndex > 0 ? decodedFullName.substring(splitIndex + 1).trim() : '',
    }
  }) || []
